import React, { ReactElement, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DefaultNamespace, TFunction } from 'i18next';
import { isNullOrUndefined } from '../../../../lib/typeGuards';
import { PersonalMessageContent, PersonalMessageLink } from '../../../../types/api/PAL/PersonalMessageList';
import { DateFormat, DateFormats } from '../../DateFormat';
import { Hyperlink } from '../../../../components/atoms/Hyperlink';
import { SpeechBalloon } from '../../SpeechBalloon';
import { Namespace } from '../../../../translation/namespace';
import { ContentLink } from '../../ContentLink';
import { ContentParagraph } from '../../ContentParagraph';
import { replaceStringHtml } from '../../../../lib/StringUtils/StringHTMLUtils';
import { ExtraProps, normalizeExtraProps } from './PersonalMessageUtils';
import { Strong } from '../../../../components/atoms/Strong/Strong';

const getSender = (senderLink: PersonalMessageLink): ReactElement =>
  isNullOrUndefined(senderLink.url)
    ? (
        <span key={senderLink.name} className="personal-message__sender">
          {senderLink.name}
        </span>
      )
    : (
        <Hyperlink key={senderLink.name} to={senderLink.url} className="personal-message__link">
          {senderLink.name}
        </Hyperlink>
      );

function getRelatedPage(relatedPageLink: PersonalMessageLink, t: any, i18n: any, subcategory?: string): ReactElement {
  if (relatedPageLink.url) {
    // returns a link to a page

    /**
     * Checking for hash links.
     * As ReactRouter does not support fragment navigation (/page#fragment) yet, we disable the routed navigation,
     * if a hash exists in the URL.
     * Usage in WatchedPageMessages
     * Downside: we have to load the whole document & its assets without SPA functions.
     * See SON-13004.
     */
    const urlHasHash = relatedPageLink.url.split('#')[1];

    return (
      <Hyperlink
        key={relatedPageLink.name}
        to={relatedPageLink.url}
        routing={!urlHasHash}
        className="personal-message__link personal-message__link--bold"
      >
        {relatedPageLink.name}
      </Hyperlink>
    );
  }

  if (subcategory !== 'teamroomDeletionSuccess') {
    return (
      <Trans
        i18nKey={`${Namespace.PersonalMessage}:deletedLink`}
        i18n={i18n}
        values={{ relatedPage: relatedPageLink.name }}
        components={{
          relatedpage: (
            <span key={`deletedLink-span-${relatedPageLink.name}`} className="personal-message__link--bold">
              {/* placeholder */}
            </span>
          ),
          bold: <Strong key={`deletedLink-strong-${relatedPageLink.name}`}>{/* placeholder */}</Strong>,
        }}
      />
    );
  }

  return (
    <span key={relatedPageLink.name} className="personal-message__link--bold">
      {relatedPageLink.name}
    </span>
  );
}

function getUserRole({ rolename }: Record<string, string>, t: any, i18n: any) {
  const roleTranslationKey = `userRole.${rolename}`;
  return i18n.exists(`${Namespace.PersonalMessage}:${roleTranslationKey}`) ? t(roleTranslationKey) : rolename;
}

/**
 * For personal messages which are created by external systems via the API, the client provides
 * a dynamic message text that may contain link placeholders which need to be resolved with hyperlinks components.
 * Example:
 * extraProps.message: "Hello from Hermes, your file {{uploadLink}} was sucessfully uploaded."
 * links: { uploadLink: { name: "son-release-99.zip", url: "http://example.net/..." }}
 */
function resolveDynamicMessageText(message: string, links: { [p: string]: PersonalMessageLink }): [string, ReactNode] {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  return replaceStringHtml(message, /{{(\w+)}}/g, (linkName) => {
    const link = links[linkName];
    const node = link.url && (
      <Hyperlink key={link.url} to={link.url} className="personal-message__link personal-message__link--bold">
        {link.name}
      </Hyperlink>
    );

    return [link.name, node];
  });
}

function getContextPage(contextPage: PersonalMessageLink) {
  return contextPage.url ? <ContentLink to={contextPage.url}>{contextPage.name}</ContentLink> : <>{contextPage.name}</>;
}

function getComponents(
  links: Record<string, PersonalMessageLink>,
  t: TFunction<DefaultNamespace, undefined>,
  i18n: any,
  subcategory: string,
) {
  const components: Record<string, any> = {};

  if (links?.sender) {
    components.sender = getSender(links.sender);
  }

  if (links?.relatedPage) {
    components.relatedpage = getRelatedPage(links.relatedPage, t, i18n, subcategory);
  }

  if (links?.contextPage) {
    components.contextpage = getContextPage(links.contextPage);
  }

  return components;
}

function getMessageText(
  t: TFunction,
  i18n: any,
  subcategory: string | undefined | null,
  extraProps: ExtraProps,
  links: Record<string, PersonalMessageLink>,
) {
  const normalizedExtraProps = normalizeExtraProps(extraProps, i18n.language);
  switch (subcategory) {
    case 'dynamicMessageText':
      return resolveDynamicMessageText(normalizedExtraProps.message, links);
    case undefined:
    case null:
      return `${extraProps?.on2MessageTitle}: ${extraProps.on2MessageContent}`;
    default:
      return (
        <Trans
          i18nKey={`${Namespace.PersonalMessage}:text.${subcategory}`}
          i18n={i18n}
          values={{
            userRole: getUserRole(normalizedExtraProps, t, i18n),
            sender: links?.sender?.name,
            relatedPage: links?.relatedPage?.name,
            contextPage: links?.contextPage?.name,
            ...normalizedExtraProps,
          }}
          components={getComponents(links, t, i18n, subcategory)}
        />
      );
  }
}

export default function PersonalMessage({
  userContent,
  links,
  subcategory,
  extraProps,
  creationDate,
}: PersonalMessageContent) {
  const { t, i18n } = useTranslation(Namespace.PersonalMessage);

  const messageText = getMessageText(t, i18n, subcategory, extraProps, links);

  return (
    <div className="personal-message">
      <ContentParagraph>{messageText}</ContentParagraph>
      <DateFormat
        date={creationDate}
        format={DateFormats.DATE_TIME}
        className="personal-message__date font-regular-14-19"
      />
      {userContent && <SpeechBalloon type={t(`${Namespace.glossary}:message`)} content={userContent} />}
    </div>
  );
}
